import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent, LiveResultHeadingType} from "@components";
import {
  CategoryItem,
  CategoryType,
  CompetitorResults,
  Country,
  EventItem,
  FullScreenMode,
  Gender,
  Lookups,
  Race,
  RaceSettings,
  ResultDisplayType
} from "@model";
import {CompetitorResultsService, LiveResultsService} from "@services";
import {environment} from "@environment";
import {Location} from "@angular/common";

@Component({
    selector: 'app-live-results-race-details',
    templateUrl: './live-results-race-details.component.html',
    styleUrls: ['./live-results-race-details.component.scss'],
    standalone: false
})
export class LiveResultsRaceDetailsComponent extends AbstractComponent implements OnInit {

  static readonly TIMEOUT_30 = 30000;
  static readonly TIMEOUT_10 = 10000;
  static readonly MAX_TIMEOUT = 2_147_483_647; // Max 32-bit signed integer in ms

  @Input() race: Race | undefined = undefined;
  @Input() event: EventItem | undefined = undefined;
  @Input() isFullScreen: boolean = false;
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input({required: true}) genders: Country[] = [];
  @Input() liveResult: CompetitorResults[] | undefined = undefined;
  @Input() measurementType: number | undefined = undefined;
  @Input() categories: CategoryItem[] = [];

  private intervals: Map<"results" | "finished" | "started", NodeJS.Timeout> = new Map();

  image: string = `${environment.assetsImagesPath}/assets/img/carousel/live_results.png`;

  name: string = "";
  gender: number | undefined = undefined;
  category: number | undefined = undefined;
  overall: string | undefined = undefined;
  distance: number | undefined = undefined;

  fullScreenMode: FullScreenMode = FullScreenMode.FULL;
  competitorLiveResults: CompetitorResults[] = [];
  filteredLiveResults: CompetitorResults[] = [];
  isRoundRace: boolean = false;
  finishedCount: number = 0;
  startedCount: number = 0;

  liveResultMale: CompetitorResults[] | undefined = undefined;
  liveResultFemale: CompetitorResults[] | undefined = undefined;
  rankingList: CompetitorResults[] = [];

  isFinished: boolean = false;
  showCategories: boolean = false;
  isCarousel: boolean = false;
  darkMode: boolean = false;
  rankingEnabled: boolean = false;
  categoriesForRanking: CategoryItem[] = [];

  constructor(
      private route: ActivatedRoute,
      private router: Router,
      public translocoService: TranslocoService,
      private location: Location,
      private resultsService: LiveResultsService,
      private competitorResultsService: CompetitorResultsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.darkMode = this.isFullScreen;
    this.showCategories =
        !!this.race?.raceSettings?.category?.categoryType &&
        this.race?.raceSettings?.category?.categoryType !== CategoryType.NOT_USED;

    if (this.isBrowser) {
      this.isCarousel = this.race?.raceSettings?.resultDisplayType === ResultDisplayType.ROTATING;
      this.measurementType = this.race?.raceSettings?.measurementType;
      this.isRoundRace = RaceSettings.isRoundRace(this.race?.raceSettings?.resultProcessingType)
          || Race.isBackyardUltra(this.race?.sport);
      this.startFetchingResults();
    }
  }

  
  // ── Fetching ──────────────────────────────────────────────────────────────

  startFetchingResults(): void {
    if (!this.race?.raceStart || !this.race?.raceEnd) return;

    const raceStart = new Date(this.race.raceStart).getTime();
    const raceEnd = new Date(this.race.raceEnd).getTime();
    const now = Date.now();

    if (now >= raceStart) {
      // If race has started, fetch the results immediately
      this.startFetchingCompetitorsResults();
      this.startFetchingFinishedCount();
      this.startFetchingStartedCount();
      // Schedule the finalization process when the race ends + 30 seconds
      this.scheduleFinalize(raceEnd + LiveResultsRaceDetailsComponent.TIMEOUT_30 - now);
    } else {
      this.scheduleStart(raceStart - now);
      this.scheduleFinalize(raceEnd + LiveResultsRaceDetailsComponent.TIMEOUT_30 - now);
    }
  }

  private startFetchingCompetitorsResults(): void {
    this.fetchResults();
    this.intervals.set("results", setInterval(() => this.fetchResults(), LiveResultsRaceDetailsComponent.TIMEOUT_10));
  }

  private startFetchingFinishedCount(): void {
    this.fetchFinishCount();
    this.intervals.set("finished", setInterval(() => this.fetchFinishCount(), LiveResultsRaceDetailsComponent.TIMEOUT_10));
  }

  private startFetchingStartedCount(): void {
    this.fetchStartedCount();
    this.intervals.set("started", setInterval(() => this.fetchStartedCount(), LiveResultsRaceDetailsComponent.TIMEOUT_10));
  }

  scheduleStart(delay: number): void {
    if (delay > LiveResultsRaceDetailsComponent.MAX_TIMEOUT) {
      setTimeout(() => this.scheduleStart(delay - LiveResultsRaceDetailsComponent.MAX_TIMEOUT),
          LiveResultsRaceDetailsComponent.MAX_TIMEOUT);
    } else {
      setTimeout(() => {
        this.startFetchingCompetitorsResults();
        this.startFetchingFinishedCount();
        this.startFetchingStartedCount();
      }, delay);
    }
  }

  fetchFinishCount(): void {
    this.resultsService.getFinishedCount(this.race?.id).subscribe(count => {
      this.finishedCount = count ?? 0;
    });
  }

  fetchStartedCount(): void {
    this.resultsService.getStartedCount(this.race?.id).subscribe(count => {
      this.startedCount = count ?? 0;
    });
  }

  fetchResults(): void {
    if (!this.race?.id) return;
    this.competitorResultsService.getAllCompetitorResults(this.race.id).subscribe(results => {
      this.competitorLiveResults = results;
      this.filterResults();
    });
  }

  stopFetching(): void {
    this.intervals.forEach((intervalId, key) => {
      clearInterval(intervalId);
      this.intervals.delete(key);
    });
  }

  scheduleFinalize(timeAfterRaceEnd: number): void {
    if (timeAfterRaceEnd > LiveResultsRaceDetailsComponent.MAX_TIMEOUT) {
      setTimeout(() => this.scheduleFinalize(timeAfterRaceEnd - LiveResultsRaceDetailsComponent.MAX_TIMEOUT),
          LiveResultsRaceDetailsComponent.MAX_TIMEOUT);
    } else {
      setTimeout(() => {
        this.isFinished = true;
        this.stopFetching();
        this.fetchResults();
        this.fetchFinishCount();
        this.fetchStartedCount();
      }, timeAfterRaceEnd);
    }
  }

  // ── Filtering ─────────────────────────────────────────────────────────────

  handleExploreResult(liveResults: CompetitorResults[]): void {
    this.competitorLiveResults = liveResults ? [...liveResults] : [];
    this.filterResults();
  }

  filterResults(): void {
    this.filteredLiveResults = [...this.competitorLiveResults];

    if (this.isFullScreen) {
      const femaleResults = this.competitorLiveResults.filter(r => r.sex === Gender.FEMALE);
      const maleResults = this.competitorLiveResults.filter(r => r.sex === Gender.MALE);
      this.liveResultFemale = [...femaleResults];
      this.liveResultMale = [...maleResults];
    }
  }


  back(): void {
    this.location.back();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.intervals.forEach((intervalId, key) => {
      clearInterval(intervalId);
      this.intervals.delete(key);
    });
  }

  onToggleDarkMode(isDarkMode: boolean) {
    if (this.isFullScreen) {
      this.darkMode = isDarkMode;
    }
  }

  onShowRankingToggle(showRanking: boolean) {
    if (this.isFullScreen) {
      this.rankingEnabled = showRanking;
      this.fullScreenMode = this.rankingEnabled ?
          FullScreenMode.FINAL_RANKING :
          FullScreenMode.FULL;
      if (this.rankingEnabled) {
        this.fullScreenMode = FullScreenMode.FINAL_RANKING;
        const existingCategoryIds = new Set(
            this.competitorLiveResults?.map(r => r?.category)
        );
        this.categoriesForRanking = this.categories.filter(c => existingCategoryIds.has(c.id));
        if (this.categoriesForRanking.length) {
          this.selectCategory(this.categoriesForRanking.at(0));
        }
        else {
          this.selectGender(Gender.MALE);
        }
      }
      else {
        this.fullScreenMode = FullScreenMode.FULL;
      }
    }
  }

  toggleView(): void {
    this.rankingEnabled = false;
    this.fullScreenMode = this.fullScreenMode === FullScreenMode.SPLIT
        ? FullScreenMode.FULL
        : FullScreenMode.SPLIT;
  }


  selectedCategory: CategoryItem | undefined;
  selectedGender: Gender | undefined;

  selectCategory(category?: CategoryItem) {
    this.selectedCategory = category
    this.selectedGender = undefined;
    if (this.selectedCategory) {
      this.filterRanking();
    }
  }

  selectGender(gender?: Gender): void {
    this.selectedGender = gender;
    this.selectedCategory = undefined;
    if (this.selectedGender) {
      this.filterRanking();
    }
  }


  filterRanking() {
    this.rankingList = this.competitorLiveResults?.filter(r => {
      if (this.selectedCategory) {
        return r?.category === this.selectedCategory.id;
      }
      if (this.selectedGender) {
        return r?.sex === this.selectedGender;
      }
      return true;
    });
  }

  protected readonly LiveResultHeadingType = LiveResultHeadingType;
  protected readonly FullScreenMode = FullScreenMode;
  protected readonly Gender = Gender;
}